import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Briefcase, Search, Shield, Clock, AlertTriangle, CheckCircle, XCircle, HelpCircle, Send, ArrowLeft, MessageSquare, ChevronLeft, Eye, Key, Copy, Trash2, Link, Info, FileText, Settings as SettingsIcon } from 'lucide-react';
import { getAdminStats, getAdminCompanies, updateAdminCompany, createAdminCompany, getPlans, updatePlan, getCompanyApiKey, createCompanyApiKey, deleteCompanyApiKey, getCompanyWebhook, saveCompanyWebhook } from '../api/admin';
import { getAdminTickets, updateAdminTicket, addAdminTicketMessage } from '../api/support';
import { Button } from '../components/ui/Button';
import { Input, Select, Textarea } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { AdminNotificationBell } from '../components/admin/AdminNotificationBell';

const PLAN_LABELS = { starter: 'Starter', business: 'Business', pro: 'Pro', enterprise: 'Enterprise' };
const PLAN_COLORS = { starter: 'bg-slate-100 text-slate-700', business: 'bg-blue-100 text-blue-700', pro: 'bg-purple-100 text-purple-700', enterprise: 'bg-amber-100 text-amber-700' };
const STATUS_LABELS = { trial: 'Deneme', active: 'Aktif', suspended: 'Askıda', cancelled: 'İptal' };
const STATUS_COLORS = { trial: 'bg-blue-100 text-blue-700', active: 'bg-green-100 text-green-700', suspended: 'bg-red-100 text-red-700', cancelled: 'bg-slate-100 text-slate-500' };

function StatCard({ icon: Icon, label, value, color = 'blue' }) {
  const bg = { blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600', orange: 'bg-orange-50 text-orange-600', red: 'bg-red-50 text-red-600', purple: 'bg-purple-50 text-purple-600' };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bg[color]}`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value ?? '—'}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export function SuperAdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [detailCompany, setDetailCompany] = useState(null);
  const [detailForm, setDetailForm] = useState({});
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailTab, setDetailTab] = useState('info');
  const [billingForm, setBillingForm] = useState({});
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({ name: '', email: '', phone: '', plan: 'starter', admin_name: '', admin_email: '', admin_password: '' });
  const [newCompanySaving, setNewCompanySaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansSaving, setPlansSaving] = useState({});
  const [activeTab, setActiveTab] = useState('companies');
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportFilter, setSupportFilter] = useState('');
  const [supportDetail, setSupportDetail] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportReply, setSupportReply] = useState('');
  const [supportSending, setSupportSending] = useState(false);
  // API Entegrasyon modal
  const [apiCompany, setApiCompany] = useState(null); // açık olan firma
  const [apiKeyInfo, setApiKeyInfo] = useState(null);
  const [apiNewKey, setApiNewKey] = useState(null); // yeni oluşturulan key (sadece bir kez gösterilir)
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [webhookForm, setWebhookForm] = useState({ url: '', secret: '', events: ['job.created', 'job.status_changed', 'job.completed', 'job.approved'] });
  const [webhookSaving, setWebhookSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        getAdminStats(),
        getAdminCompanies({ status: statusFilter || undefined, search: search || undefined }),
      ]);
      setStats(s);
      setCompanies(c.companies);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ plan: c.plan, subscription_status: c.subscription_status, max_users: c.max_users });
  };

  const saveEdit = async (id) => {
    try {
      await updateAdminCompany(id, editForm);
      setEditingId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Güncelleme başarısız');
    }
  };

  const quickAction = async (id, data) => {
    try {
      await updateAdminCompany(id, data);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'İşlem başarısız');
    }
  };

  const openApiModal = async (company) => {
    setApiCompany(company);
    setApiNewKey(null);
    setApiKeyLoading(true);
    try {
      const [keyData, whData] = await Promise.all([
        getCompanyApiKey(company.id),
        getCompanyWebhook(company.id),
      ]);
      setApiKeyInfo(keyData);
      if (whData.hasWebhook) {
        setWebhookInfo(whData);
        setWebhookForm({ url: whData.url || '', secret: '', events: whData.events || [] });
      } else {
        setWebhookInfo(null);
        setWebhookForm({ url: '', secret: '', events: ['job.created', 'job.status_changed', 'job.completed', 'job.approved'] });
      }
    } catch { setApiKeyInfo({ hasKey: false }); setWebhookInfo(null); }
    setApiKeyLoading(false);
  };

  const handleCreateApiKey = async () => {
    if (apiKeyInfo?.hasKey && !window.confirm('Mevcut API key iptal edilecek ve yeni key oluşturulacak. Devam?')) return;
    setApiKeyLoading(true);
    try {
      const data = await createCompanyApiKey(apiCompany.id);
      setApiNewKey(data.key);
      setApiKeyInfo({ hasKey: true, key_prefix: data.key_prefix, name: data.name, permissions: data.permissions, created_at: data.created_at, last_used_at: null });
    } catch (err) { alert(err.response?.data?.error || 'Hata'); }
    setApiKeyLoading(false);
  };

  const handleDeleteApiKey = async () => {
    if (!window.confirm('API key silinecek. Bu key ile yapılan tüm entegrasyonlar çalışmayı durduracak.')) return;
    try {
      await deleteCompanyApiKey(apiCompany.id);
      setApiKeyInfo({ hasKey: false });
      setApiNewKey(null);
    } catch {}
  };

  const handleSaveWebhook = async () => {
    if (!webhookForm.url) return alert('URL gerekli');
    const secret = webhookForm.secret || Array.from(crypto.getRandomValues(new Uint8Array(24))).map(b => b.toString(16).padStart(2, '0')).join('');
    setWebhookSaving(true);
    try {
      const data = await saveCompanyWebhook(apiCompany.id, { url: webhookForm.url, secret, events: webhookForm.events });
      setWebhookInfo(data.webhook);
      setWebhookForm(f => ({ ...f, secret: '' }));
    } catch (err) { alert(err.response?.data?.error || 'Hata'); }
    setWebhookSaving(false);
  };

  const trialDays = (ends) => {
    if (!ends) return null;
    const d = Math.ceil((new Date(ends) - new Date()) / 86400000);
    return d;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-slate-400 hover:text-white transition text-sm">
            <ChevronLeft size={16} /> Ana Panel
          </button>
          <div className="w-px h-5 bg-slate-700" />
          <Shield size={20} className="text-amber-400" />
          <h1 className="text-lg font-bold">Fieldpin Super Admin</h1>
          <div className="ml-auto flex items-center gap-3">
            <AdminNotificationBell />
            <span className="text-xs text-slate-400">admin.fieldpin.io</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-slate-200 p-1 w-fit">
          <button onClick={() => setActiveTab('companies')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'companies' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
            <Building2 size={14} className="inline mr-1.5 -mt-0.5" /> Firmalar
          </button>
          <button onClick={async () => { setActiveTab('support'); try { const d = await getAdminTickets({ status: supportFilter || undefined }); setSupportTickets(d.tickets); } catch {} }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'support' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
            <HelpCircle size={14} className="inline mr-1.5 -mt-0.5" /> Destek Talepleri
          </button>
          <button onClick={async () => { setActiveTab('pricing'); try { const d = await getPlans(); setPlans(d.plans); } catch {} }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'pricing' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
            Ücretlendirme
          </button>
        </div>

        {activeTab === 'pricing' ? (
          <div className="space-y-4">
            {plans.map(p => (
              <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">{p.display_name} <span className="text-xs text-slate-400 font-normal ml-1">({p.name})</span></h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
                <div className="grid grid-cols-5 gap-4">
                  <Input label="Aylık Fiyat (TL)" type="number" value={p.price_monthly} onChange={e => setPlans(ps => ps.map(x => x.id === p.id ? { ...x, price_monthly: e.target.value } : x))} />
                  <Input label="Yıllık İndirim (%)" type="number" min="0" max="100" value={p.yearly_discount_percent ?? 20} onChange={e => setPlans(ps => ps.map(x => x.id === p.id ? { ...x, yearly_discount_percent: parseInt(e.target.value) || 0 } : x))} />
                  <Input label="Max Kullanıcı" type="number" value={p.max_users} onChange={e => setPlans(ps => ps.map(x => x.id === p.id ? { ...x, max_users: parseInt(e.target.value) || 0 } : x))} />
                  <Input label="Deneme (gün)" type="number" value={p.trial_days} onChange={e => setPlans(ps => ps.map(x => x.id === p.id ? { ...x, trial_days: parseInt(e.target.value) || 0 } : x))} />
                  <div className="flex items-end">
                    <Button size="sm" loading={plansSaving[p.id]} onClick={async () => {
                      setPlansSaving(s => ({ ...s, [p.id]: true }));
                      try { await updatePlan(p.id, { price_monthly: parseFloat(p.price_monthly), max_users: p.max_users, trial_days: p.trial_days, yearly_discount_percent: p.yearly_discount_percent ?? 20 }); } catch {}
                      setPlansSaving(s => ({ ...s, [p.id]: false }));
                    }}>Kaydet</Button>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">Yıllık: {Math.round(parseFloat(p.price_monthly || 0) * 12 * (1 - (p.yearly_discount_percent ?? 20) / 100)).toLocaleString('tr-TR')} TL/yıl ({p.yearly_discount_percent ?? 20}% indirimli)</p>
              </div>
            ))}
          </div>
        ) : activeTab === 'support' ? (
          <SuperAdminSupport
            tickets={supportTickets} setTickets={setSupportTickets}
            filter={supportFilter} setFilter={setSupportFilter}
            detail={supportDetail} setDetail={setSupportDetail}
            messages={supportMessages} setMessages={setSupportMessages}
            reply={supportReply} setReply={setSupportReply}
            sending={supportSending} setSending={setSupportSending}
          />
        ) : <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <StatCard icon={Building2} label="Toplam Firma" value={stats.companies.total} color="blue" />
            <StatCard icon={Clock} label="Trial" value={stats.companies.trial} color="orange" />
            <StatCard icon={CheckCircle} label="Aktif" value={stats.companies.active} color="green" />
            <StatCard icon={AlertTriangle} label="Trial Süresi Dolan" value={stats.companies.trial_expired} color="red" />
            <StatCard icon={Users} label="Toplam Kullanıcı" value={stats.total_users} color="purple" />
            <StatCard icon={Briefcase} label="Toplam İş" value={stats.total_jobs} color="blue" />
          </div>
        )}

        {/* Hızlı Erişim Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <button
            onClick={() => navigate('/ops-center-f9x2/legal')}
            className="bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-500 hover:shadow-sm transition text-left flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <FileText size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">Yasal Dokümanlar</p>
              <p className="text-xs text-slate-500">KVKK, Sözleşmeler, Politikalar</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/ops-center-f9x2/quotes')}
            className="bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-500 hover:shadow-sm transition text-left flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <FileText size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">Özel Teklifler</p>
              <p className="text-xs text-slate-500">Enterprise için custom quote</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/ops-center-f9x2/payment-settings')}
            className="bg-white rounded-xl border border-slate-200 p-4 hover:border-purple-500 hover:shadow-sm transition text-left flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <SettingsIcon size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">Ödeme Ayarları</p>
              <p className="text-xs text-slate-500">PayTR, KDV, iade süresi</p>
            </div>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <form onSubmit={handleSearch} className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Firma adı, slug veya e-posta ara..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" />
            </div>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Tüm Durumlar</option>
              <option value="trial">Deneme</option>
              <option value="active">Aktif</option>
              <option value="suspended">Askıda</option>
              <option value="cancelled">İptal</option>
            </Select>
            <Button type="submit" size="sm">Ara</Button>
            <Button size="sm" onClick={() => setNewCompanyOpen(true)}>+ Yeni Firma</Button>
          </form>
        </div>

        {/* Company Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-500 border-b">
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                  <th className="px-4 py-3 font-medium text-center">Kullanıcı</th>
                  <th className="px-4 py-3 font-medium text-center">İş</th>
                  <th className="px-4 py-3 font-medium">Trial</th>
                  <th className="px-4 py-3 font-medium">Kayıt</th>
                  <th className="px-4 py-3 font-medium text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(c => {
                  const days = trialDays(c.trial_ends_at);
                  const isEditing = editingId === c.id;
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3 cursor-pointer" onClick={() => {
                        setDetailCompany(c);
                        setDetailTab('info');
                        setDetailForm({
                          plan: c.plan, subscription_status: c.subscription_status,
                          max_users: c.max_users, trial_ends_at: c.trial_ends_at ? c.trial_ends_at.slice(0, 10) : '',
                        });
                        setBillingForm({
                          billing_name: c.billing_name || '', billing_tax_number: c.billing_tax_number || '',
                          billing_tax_office: c.billing_tax_office || '', billing_address: c.billing_address || '',
                          billing_city: c.billing_city || '', billing_district: c.billing_district || '',
                          billing_zip: c.billing_zip || '', billing_email: c.billing_email || '',
                          billing_phone: c.billing_phone || '',
                        });
                      }}>
                        <p className="font-medium text-slate-800 hover:text-blue-600 transition">{c.name}</p>
                        <p className="text-xs text-slate-400">{c.slug} · {c.email} · <span className="font-mono">{c.id.slice(0, 8)}</span></p>
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select value={editForm.plan} onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))}
                            className="border rounded px-2 py-1 text-xs">
                            {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PLAN_COLORS[c.plan]}`}>
                            {PLAN_LABELS[c.plan]}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select value={editForm.subscription_status} onChange={e => setEditForm(f => ({ ...f, subscription_status: e.target.value }))}
                            className="border rounded px-2 py-1 text-xs">
                            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[c.subscription_status]}`}>
                            {STATUS_LABELS[c.subscription_status]}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isEditing ? (
                          <input type="number" min="1" value={editForm.max_users}
                            onChange={e => setEditForm(f => ({ ...f, max_users: parseInt(e.target.value) }))}
                            className="border rounded px-2 py-1 text-xs w-16 text-center" />
                        ) : (
                          <span>{c.user_count} <span className="text-slate-400">/ {c.max_users}</span></span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">{c.job_count}</td>
                      <td className="px-4 py-3">
                        {c.subscription_status === 'trial' && days !== null ? (
                          <span className={`text-xs font-medium ${days <= 0 ? 'text-red-600' : days <= 5 ? 'text-orange-600' : 'text-slate-600'}`}>
                            {days <= 0 ? 'Doldu' : `${days} gün`}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {new Date(c.created_at).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" onClick={() => saveEdit(c.id)}>Kaydet</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>İptal</Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-800 hover:bg-purple-50" onClick={() => openApiModal(c)}>
                              <Key size={14} className="mr-1" /> API
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => startEdit(c)}>Düzenle</Button>
                            {c.subscription_status !== 'suspended' ? (
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => quickAction(c.id, { subscription_status: 'suspended' })}>
                                Askıya Al
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => quickAction(c.id, { subscription_status: 'active' })}>
                                Aktif Et
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {companies.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-400">Firma bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </>}

        {/* Firma Detay Modal */}
        {/* Firma Detay Modal */}
        <Modal open={!!detailCompany} onClose={() => setDetailCompany(null)} title="Firma Detayı" size="lg">
          {detailCompany && (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex gap-1 bg-slate-50 rounded-lg p-1">
                {[['info', 'Bilgiler'], ['billing', 'Fatura']].map(([k, l]) => (
                  <button key={k} onClick={() => setDetailTab(k)}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${detailTab === k ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>{l}</button>
                ))}
              </div>

              {detailTab === 'info' && (<>
                <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                  <p className="text-sm"><span className="text-slate-500 w-20 inline-block">Firma:</span> <strong>{detailCompany.name}</strong></p>
                  <p className="text-sm flex items-center gap-2">
                    <span className="text-slate-500 w-20 inline-block">ID:</span>
                    <code className="text-xs bg-slate-200 px-1.5 py-0.5 rounded font-mono select-all">{detailCompany.id}</code>
                    <button onClick={(e) => { navigator.clipboard.writeText(detailCompany.id); const b = e.currentTarget; b.textContent = 'Kopyalandı!'; b.classList.add('text-green-600'); setTimeout(() => { b.textContent = 'Kopyala'; b.classList.remove('text-green-600'); }, 1500); }}
                      className="text-xs text-blue-500 hover:text-blue-700 transition">Kopyala</button>
                  </p>
                  <p className="text-sm"><span className="text-slate-500 w-20 inline-block">Email:</span> {detailCompany.email || '—'}</p>
                  <p className="text-sm"><span className="text-slate-500 w-20 inline-block">Telefon:</span> {detailCompany.phone || '—'}</p>
                  <p className="text-sm"><span className="text-slate-500 w-20 inline-block">Slug:</span> <code className="text-xs bg-slate-200 px-1 rounded">{detailCompany.slug}</code></p>
                  <p className="text-sm"><span className="text-slate-500 w-20 inline-block">Kayıt:</span> {new Date(detailCompany.created_at).toLocaleDateString('tr-TR')}</p>
                  <p className="text-sm"><span className="text-slate-500 w-20 inline-block">Kullanıcı:</span> {detailCompany.user_count} / {detailCompany.max_users}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Select label="Plan" value={detailForm.plan} onChange={e => setDetailForm(f => ({ ...f, plan: e.target.value }))}>
                    {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Select>
                  <Select label="Durum" value={detailForm.subscription_status} onChange={e => setDetailForm(f => ({ ...f, subscription_status: e.target.value }))}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Max Kullanıcı" type="number" min="1" value={detailForm.max_users}
                    onChange={e => setDetailForm(f => ({ ...f, max_users: parseInt(e.target.value) || 1 }))} />
                  <Input label="Trial Bitiş" type="date" value={detailForm.trial_ends_at}
                    onChange={e => setDetailForm(f => ({ ...f, trial_ends_at: e.target.value }))} />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="secondary" onClick={() => setDetailCompany(null)}>İptal</Button>
                  <Button loading={detailSaving} onClick={async () => {
                    setDetailSaving(true);
                    try { await updateAdminCompany(detailCompany.id, { plan: detailForm.plan, subscription_status: detailForm.subscription_status, max_users: detailForm.max_users }); setDetailCompany(null); load(); }
                    catch (err) { alert(err.response?.data?.error || 'Güncelleme başarısız'); }
                    setDetailSaving(false);
                  }}>Kaydet</Button>
                </div>
              </>)}

              {detailTab === 'billing' && (<>
                <div className="space-y-3">
                  <Input label="Firma Ünvanı" value={billingForm.billing_name} onChange={e => setBillingForm(f => ({ ...f, billing_name: e.target.value }))} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Vergi No" value={billingForm.billing_tax_number} onChange={e => setBillingForm(f => ({ ...f, billing_tax_number: e.target.value }))} />
                    <Input label="Vergi Dairesi" value={billingForm.billing_tax_office} onChange={e => setBillingForm(f => ({ ...f, billing_tax_office: e.target.value }))} />
                  </div>
                  <Input label="Adres" value={billingForm.billing_address} onChange={e => setBillingForm(f => ({ ...f, billing_address: e.target.value }))} />
                  <div className="grid grid-cols-3 gap-3">
                    <Input label="İl" value={billingForm.billing_city} onChange={e => setBillingForm(f => ({ ...f, billing_city: e.target.value }))} />
                    <Input label="İlçe" value={billingForm.billing_district} onChange={e => setBillingForm(f => ({ ...f, billing_district: e.target.value }))} />
                    <Input label="Posta Kodu" value={billingForm.billing_zip} onChange={e => setBillingForm(f => ({ ...f, billing_zip: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Fatura E-posta" value={billingForm.billing_email} onChange={e => setBillingForm(f => ({ ...f, billing_email: e.target.value }))} />
                    <Input label="Fatura Telefon" value={billingForm.billing_phone} onChange={e => setBillingForm(f => ({ ...f, billing_phone: e.target.value }))} />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="secondary" onClick={() => setDetailCompany(null)}>İptal</Button>
                  <Button loading={detailSaving} onClick={async () => {
                    setDetailSaving(true);
                    try { await updateAdminCompany(detailCompany.id, billingForm); setDetailCompany(null); load(); }
                    catch (err) { alert(err.response?.data?.error || 'Güncelleme başarısız'); }
                    setDetailSaving(false);
                  }}>Kaydet</Button>
                </div>
              </>)}
            </div>
          )}
        </Modal>

        {/* API Entegrasyon Modal */}
        <Modal open={!!apiCompany} onClose={() => { setApiCompany(null); setApiNewKey(null); }} title={`API Entegrasyonu — ${apiCompany?.name || ''}`} size="lg">
          {apiCompany && (
            <div className="space-y-5">
              {apiKeyLoading ? (
                <div className="flex justify-center py-8"><div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : (<>
                {/* Yeni oluşturulan key uyarısı */}
                {apiNewKey && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-green-800 mb-2">API Key Oluşturuldu</p>
                    <div className="flex items-center gap-2 bg-white border border-green-300 rounded-lg px-4 py-3">
                      <code className="flex-1 text-sm font-mono text-slate-800 break-all select-all">{apiNewKey}</code>
                      <button onClick={() => { navigator.clipboard.writeText(apiNewKey); }}
                        className="shrink-0 p-1.5 rounded-md hover:bg-slate-100 transition" title="Kopyala">
                        <Copy size={16} className="text-slate-500" />
                      </button>
                    </div>
                    <p className="text-xs text-red-600 font-medium mt-2">Bu key sadece bir kez gösterilir. Kopyalayıp güvenli bir yere kaydedin.</p>
                  </div>
                )}

                {/* Key bilgisi */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Key size={14} /> API Key</h4>
                  {apiKeyInfo?.hasKey ? (
                    <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-mono text-slate-600">{apiKeyInfo.key_prefix}{'••••••••••••••••••••••••••••'}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Oluşturulma: {new Date(apiKeyInfo.created_at).toLocaleDateString('tr-TR')}
                            {' · '}
                            Son kullanım: {apiKeyInfo.last_used_at ? new Date(apiKeyInfo.last_used_at).toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : 'Henüz kullanılmadı'}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(apiKeyInfo.permissions || []).map(p => (
                          <span key={p} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded-full">{p}</span>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" variant="secondary" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleCreateApiKey}>
                          Yeni Key Oluştur
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={handleDeleteApiKey}>
                          <Trash2 size={14} className="mr-1" /> Sil
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-6 text-center">
                      <p className="text-sm text-slate-500 mb-3">Bu firma için henüz API key oluşturulmamış.</p>
                      <Button onClick={handleCreateApiKey}>API Key Oluştur</Button>
                    </div>
                  )}
                </div>

                {/* Webhook bölümü */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Link size={14} /> Webhook</h4>
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <Input label="Webhook URL" placeholder="https://api.printcrm.com/webhooks/fieldpin"
                      value={webhookForm.url} onChange={e => setWebhookForm(f => ({ ...f, url: e.target.value }))} />
                    <Input label="Secret" placeholder={webhookInfo ? '(mevcut secret korunacak — değiştirmek için yeni girin)' : 'Otomatik oluşturulacak'}
                      value={webhookForm.secret} onChange={e => setWebhookForm(f => ({ ...f, secret: e.target.value }))} />
                    <div>
                      <p className="text-xs font-medium text-slate-600 mb-2">Event'ler</p>
                      <div className="flex flex-wrap gap-2">
                        {['job.created', 'job.status_changed', 'job.completed', 'job.approved'].map(ev => (
                          <label key={ev} className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={webhookForm.events.includes(ev)}
                              onChange={e => {
                                setWebhookForm(f => ({
                                  ...f,
                                  events: e.target.checked ? [...f.events, ev] : f.events.filter(x => x !== ev),
                                }));
                              }}
                              className="rounded border-slate-300" />
                            <span className="text-xs text-slate-700">{ev}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <Button size="sm" onClick={handleSaveWebhook} loading={webhookSaving}>Webhook Kaydet</Button>
                    {webhookInfo && (
                      <p className="text-xs text-green-600">Aktif webhook mevcut: {webhookInfo.url}</p>
                    )}
                  </div>
                </div>

                {/* Entegrasyon bilgi kutusu */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex gap-2">
                    <Info size={16} className="text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-800 mb-1">PrintCRM Ayarları</p>
                      <p className="text-xs text-blue-700 mb-1">API URL: <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono">https://api.fieldpin.io</code></p>
                      <p className="text-xs text-blue-700 mb-2">API Key: <span className="text-blue-500">yukarıda oluşturulan key</span></p>
                      <p className="text-xs text-blue-600">Bu bilgileri PrintCRM → Ayarlar → Entegrasyonlar → FieldPin bölümüne girin.</p>
                    </div>
                  </div>
                </div>
              </>)}
            </div>
          )}
        </Modal>

        {/* Yeni Firma Modal */}
        <Modal open={newCompanyOpen} onClose={() => setNewCompanyOpen(false)} title="Yeni Firma Oluştur" size="md">
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-600">Firma Bilgileri</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Firma Adı *" value={newCompanyForm.name} onChange={e => setNewCompanyForm(f => ({ ...f, name: e.target.value }))} />
              <Input label="E-posta *" type="email" value={newCompanyForm.email} onChange={e => setNewCompanyForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Telefon" value={newCompanyForm.phone} onChange={e => setNewCompanyForm(f => ({ ...f, phone: e.target.value }))} />
              <Select label="Plan" value={newCompanyForm.plan} onChange={e => setNewCompanyForm(f => ({ ...f, plan: e.target.value }))}>
                {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </div>
            <h4 className="text-sm font-semibold text-slate-600 pt-2">Admin Kullanıcı</h4>
            <Input label="Ad Soyad *" value={newCompanyForm.admin_name} onChange={e => setNewCompanyForm(f => ({ ...f, admin_name: e.target.value }))} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="E-posta *" type="email" value={newCompanyForm.admin_email} onChange={e => setNewCompanyForm(f => ({ ...f, admin_email: e.target.value }))} />
              <Input label="Şifre *" type="password" value={newCompanyForm.admin_password} onChange={e => setNewCompanyForm(f => ({ ...f, admin_password: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setNewCompanyOpen(false)}>İptal</Button>
              <Button loading={newCompanySaving} onClick={async () => {
                setNewCompanySaving(true);
                try {
                  await createAdminCompany(newCompanyForm);
                  setNewCompanyOpen(false);
                  setNewCompanyForm({ name: '', email: '', phone: '', plan: 'starter', admin_name: '', admin_email: '', admin_password: '' });
                  load();
                } catch (err) { alert(err.response?.data?.error || 'Oluşturma başarısız'); }
                setNewCompanySaving(false);
              }}>Firma Oluştur</Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}

// ────────────────────────────────────
// Super Admin — Destek Talepleri
// ────────────────────────────────────
const TICKET_STATUS = {
  open:        { label: 'Açık',        color: 'bg-amber-100 text-amber-700',   tooltip: 'Açık - Yanıt bekleniyor' },
  in_progress: { label: 'İnceleniyor', color: 'bg-blue-100 text-blue-700',     tooltip: 'İnceleniyor - Destek ekibi ilgileniyor' },
  resolved:    { label: 'Çözüldü',     color: 'bg-green-100 text-green-700',   tooltip: 'Çözüldü' },
  closed:      { label: 'Arşivlendi',  color: 'bg-purple-100 text-purple-600', tooltip: 'Kapatıldı - Çözümlendi ve arşivlendi' },
};
const TICKET_PRIORITY = {
  low:    { label: 'Düşük',  color: 'bg-slate-100 text-slate-600' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
  high:   { label: 'Yüksek', color: 'bg-red-100 text-red-600' },
};

function SuperAdminSupport({ tickets, setTickets, filter, setFilter, detail, setDetail, messages, setMessages, reply, setReply, sending, setSending }) {
  const loadTickets = async (f) => {
    try {
      const d = await getAdminTickets({ status: f || undefined });
      setTickets(d.tickets);
    } catch {}
  };

  const openDetail = async (id) => {
    try {
      const resp = await fetch(`/api/admin/support/tickets/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await resp.json();
      setDetail(d.ticket);
      setMessages(d.messages);
    } catch {}
  };

  const handleReply = async () => {
    if (!reply.trim() || !detail) return;
    setSending(true);
    try {
      await addAdminTicketMessage(detail.id, { message: reply });
      setReply('');
      await openDetail(detail.id);
    } catch {}
    setSending(false);
  };

  const handleStatusChange = async (status) => {
    if (!detail) return;
    try {
      await updateAdminTicket(detail.id, { status });
      await openDetail(detail.id);
      loadTickets(filter);
    } catch {}
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  if (detail) {
    return (
      <div>
        <button onClick={() => { setDetail(null); loadTickets(filter); }}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft size={16} /> Geri
        </button>
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">{detail.subject}</h2>
              <p className="text-sm text-slate-500">{detail.company_name} &middot; {detail.user_name} ({detail.user_email})</p>
              <p className="text-xs text-slate-400 mt-1">{fmtDate(detail.created_at)}</p>
            </div>
            <div className="flex gap-2 items-center">
              <Select value={detail.priority} onChange={async e => { try { await updateAdminTicket(detail.id, { priority: e.target.value }); await openDetail(detail.id); } catch {} }} className="text-xs">
                <option value="low">Düşük</option>
                <option value="normal">Normal</option>
                <option value="high">Yüksek</option>
              </Select>
              <Select value={detail.status} onChange={e => handleStatusChange(e.target.value)} className="text-xs">
                <option value="open">Açık</option>
                <option value="in_progress">İnceleniyor</option>
                <option value="resolved">Çözüldü</option>
                <option value="closed">Kapatıldı</option>
              </Select>
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">{detail.message}</div>
          {detail.admin_notes && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
              <strong>Admin notu:</strong> {detail.admin_notes}
            </div>
          )}
        </div>

        <div className="space-y-3 mb-4">
          {messages.map(m => (
            <div key={m.id} className={`p-4 rounded-xl text-sm ${m.sender_type === 'admin' ? 'bg-green-50 border border-green-200 ml-8' : 'bg-white border border-slate-200 mr-8'}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-slate-800">{m.sender_name} {m.sender_type === 'admin' && <span className="text-xs text-green-600">(Admin)</span>}</span>
                <span className="text-xs text-slate-400">{fmtDate(m.created_at)}</span>
              </div>
              <p className="text-slate-700 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>

        {detail.status !== 'closed' && (
          <div className="flex gap-2">
            <input className="flex-1 border border-slate-200 rounded-lg px-4 py-2 text-sm" placeholder="Yanıt yaz..."
              value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReply()} />
            <Button onClick={handleReply} loading={sending} disabled={!reply.trim()}>
              <Send size={16} /> Yanıtla
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <Select value={filter} onChange={e => { setFilter(e.target.value); loadTickets(e.target.value); }}>
          <option value="">Tüm Durumlar</option>
          {Object.entries(TICKET_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </Select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-500 border-b">
              <th className="px-4 py-3 font-medium">Konu</th>
              <th className="px-4 py-3 font-medium">Firma</th>
              <th className="px-4 py-3 font-medium">Kullanıcı</th>
              <th className="px-4 py-3 font-medium text-center">Öncelik</th>
              <th className="px-4 py-3 font-medium text-center">Durum</th>
              <th className="px-4 py-3 font-medium text-center">Mesaj</th>
              <th className="px-4 py-3 font-medium">Tarih</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id} onClick={() => openDetail(t.id)} className="border-b hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 font-medium text-slate-800">{t.subject}</td>
                <td className="px-4 py-3 text-slate-600">{t.company_name}</td>
                <td className="px-4 py-3 text-slate-600">{t.user_name}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${TICKET_PRIORITY[t.priority]?.color}`}>{TICKET_PRIORITY[t.priority]?.label}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${TICKET_STATUS[t.status]?.color}`} title={TICKET_STATUS[t.status]?.tooltip}>{TICKET_STATUS[t.status]?.label}</span>
                </td>
                <td className="px-4 py-3 text-center text-slate-400">{t.message_count}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(t.created_at)}</td>
              </tr>
            ))}
            {tickets.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400">Destek talebi yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
